<template>
  <editable-map
    class="app-map"
    ref="map"
    editable
    :bounds="latLngBounds"
    :options="mapOptions"
    v-bind="$attrs"
    v-on="$listeners"
    @editable:drawing:commit="onAreaCreated"
    @pdf:change="onPdfChange"
    v-resize="redraw"
  >
    <slot name="prepend"></slot>
    <slot name="controls">
      <l-control-zoom position="topright" zoom-out-text="−" />
      <map-full-screen-control position="topright" />
      <l-control position="topleft" v-show="isClearable && !!isSomeValueOnMap">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <ng-button
              v-bind="attrs"
              v-on="on"
              icon
              icon-name="mdi-delete"
              @click="clearValue"
              color="light"
              :aria-label="$t('product.custom.clear')"
            />
          </template>
          {{ $t('product.custom.clear') }}
        </v-tooltip>
      </l-control>
    </slot>
    <l-geo-json v-for="(layer, index) in layers" :key="index" :geojson="layer.geometry" :optionsStyle="layer.style" />

    <template v-if="isEditableLocal">
      <editable-polygon
        v-if="drawnValue && !isRectangleDrawingMode"
        :lat-lngs="drawnValue"
        editable
        color="#00b77e"
        fill-color="#00b77e"
        @editable:vertex:dragend="onAreaChanged"
        @editable:vertex:new="onAreaChanged"
        @editable:vertex:deleted="onAreaChanged"
      ></editable-polygon>

      <editable-rectangle
        v-if="drawnValue && isRectangleDrawingMode"
        :bounds="drawnValue"
        editable
        color="#00b77e"
        fill-color="#00b77e"
        @editable:vertex:dragend="onAreaChanged"
      ></editable-rectangle>

      <app-cursor-tooltip v-if="isTooltipShown && !isRectangleDrawingMode">
        {{ $t('product.custom.completePolygon') }}
      </app-cursor-tooltip>

      <app-cursor-tooltip v-if="!!this.editableLayer && isRectangleDrawingMode">
        {{ $t('product.custom.completeRectangle') }}
      </app-cursor-tooltip>
    </template>
    <l-geo-json v-else-if="value && value.geometry" :geojson="value.geometry" :optionsStyle="valueStyle" />

    <l-tile-layer v-if="basemapUrl" :url="basemapUrl" :attribution="attribution" />

    <v-progress-circular
      v-if="isLoading"
      indeterminate
      color="primary"
      style="z-index: 20000; left: 0; right: 0; top: 0; bottom: 0; margin: auto; position: absolute"
    ></v-progress-circular>
    <slot name="append"></slot>
  </editable-map>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import { LTileLayer, LGeoJson, LControlZoom, LControl } from 'vue2-leaflet';
import { EditableMap, EditablePolygon, EditableRectangle } from 'vue2-leaflet-editable';
import { debounce } from 'lodash-es';
import mapService from '@/services/mapService';
import AppCursorTooltip from '@/components/app/AppCursorTooltip.vue';
import MapFullScreenControl from './MapFullScreenControl/MapFullScreenControl.vue';

export default {
  components: {
    EditableMap,
    EditablePolygon,
    EditableRectangle,
    LTileLayer,
    LGeoJson,
    LControl,
    LControlZoom,
    MapFullScreenControl,
    AppCursorTooltip,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    layers: {
      type: Array,
      default: () => [],
    },
    basemapUrl: {
      type: String,
    },
    bounds: {
      type: Object,
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
    isRectangleDrawingMode: {
      type: Boolean,
      default: true,
    },
    isClearable: {
      type: Boolean,
      default: false,
    },
    valueStyle: {
      type: Object,
      default: () => ({
        weight: 2,
        fillOpacity: 0.5,
        color: '#7c6e4c',
      }),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mapObject: null,
      mapOptions: {
        zoomControl: false,
      },
      attribution:
        '&copy; <a target="_blank" href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>',
      pdfSheetPlugin: null,

      // temporary layer created for drawing
      // set to null when drawing is not started or completed
      editableLayer: null,

      // drawn geometry value when drawing is completed
      drawnValue: null,
    };
  },
  computed: {
    ...mapState('map', ['plugins', 'mapWarnings']),
    ...mapGetters('map', ['drawingMode']),
    isEditableLocal() {
      return this.isEditable && !this.isPdfInPlugins;
    },
    pdfSheetPluginOptions() {
      return mapService.formatPdfOptions(this.pdfSheetPlugin.options, this.pdfSheetPlugin._rec);
    },
    isPdfInPlugins() {
      return !!this.plugins && this.plugins.filter((x) => x.id === 'pdf').length !== 0;
    },
    isTooltipShown() {
      return mapService.shouldCompletePolygonHintBeShown({
        layer: this.editableLayer,
        isRectangleDrawingMode: this.isRectangleDrawingMode,
      });
    },
    latLngBounds() {
      return this.bounds && mapService.regionBoundsToLatLngBounds(this.bounds);
    },
    isSomeValueOnMap() {
      return (
        !!this.value ||
        (this.editableLayer &&
          mapService.isSomethingDrawn({
            layer: this.editableLayer,
            isRectangleDrawingMode: this.isRectangleDrawingMode,
          }))
      );
    },
  },
  watch: {
    value(val, oldVal) {
      if (!val || !oldVal) {
        // if value changed from or to falsy value
        this.checkPdfPlugin();
      }
      if (val && !!this.editableLayer) {
        this.stopDrawing();
      }
    },
    isSomeValueOnMap(val) {
      if (!val && this.isEditableLocal && !this.editableLayer) {
        this.$nextTick(this.startDrawing);
      }
    },
    isRectangleDrawingMode() {
      if (this.isSomeValueOnMap) {
        this.clearValue();
      }
      if (this.isEditableLocal) {
        this.$nextTick(this.startDrawing);
      }
    },
    plugins: {
      handler() {
        this.checkPdfPlugin();
      },
    },
    editableLayer(val, oldVal) {
      if (!val && !!oldVal) mapService.removeLayer(this.mapObject, oldVal);
    },
    isTooltipShown(val) {
      const warningMessage = this.$t('product.custom.completePolygonExtended');
      if (val && !this.mapWarnings.includes(warningMessage)) {
        this.ADD_MAP_WARNING(warningMessage);
      }
      if (!val) {
        this.REMOVE_MAP_WARNING(warningMessage);
      }
    },
  },
  mounted() {
    this.mapObject = mapService.getMapObject(this.$refs.map);
    this.$emit('update:zoom', this.mapObject.getZoom());
    if (!this.value && this.isEditableLocal) {
      this.startDrawing();
    }

    // calculate drawnValue once here, computed drawnValue doesn't work - lat-lngs is not reactive
    if (this.isEditableLocal && this.value && this.value.geometry)
      this.drawnValue = this.getLatLngValue(this.value.geometry);
    this.checkPdfPlugin();
  },
  methods: {
    ...mapActions('map', ['updateMapPlugin']),
    ...mapMutations('map', ['ADD_MAP_WARNING', 'REMOVE_MAP_WARNING']),
    onAreaChanged(e) {
      const currentGeojson = mapService.toGeoJSON(e.layer);

      const isAllPointsSame = this.isRectangleDrawingMode ? mapService.isZeroSizePolygon(currentGeojson) : false;

      const sanitizedGeoJson =
        isAllPointsSame && this.isRectangleDrawingMode
          ? mapService.fixZeroSizeRectangle(currentGeojson, this.mapObject.getZoom())
          : currentGeojson;

      if (this.editableLayer) {
        this.drawnValue = this.getLatLngValue(sanitizedGeoJson);
        this.editableLayer = null;
      }
      this.$emit('input', {
        geometry: sanitizedGeoJson,
        meta: { source: 'drawing', regionData: { mode: this.drawingMode } },
      });
    },
    onAreaCreated(e) {
      this.onAreaChanged(e);
      if (window.ym) window.ym(42050389, 'reachGoal', 'drawing-completed', { drawingMode: this.drawingMode });
    },
    startDrawing() {
      if (this.mapObject) {
        this.stopDrawing();
        this.$nextTick(() => {
          if (this.isRectangleDrawingMode) {
            this.editableLayer = mapService.startRectangle(this.mapObject);
            if (window.ym) window.ym(42050389, 'reachGoal', 'draw-rectangle');
          } else {
            this.editableLayer = mapService.startPolygon(this.mapObject);
            if (window.ym) window.ym(42050389, 'reachGoal', 'draw-polygon');
          }
        });
      }
    },
    stopDrawing() {
      mapService.stopDrawing(this.mapObject);
      if (this.editableLayer) this.editableLayer = null;
      if (this.drawnValue) this.drawnValue = null;
    },
    clearValue() {
      this.stopDrawing();
      this.$emit('input', null);
      this.$emit('cleared');
    },
    getLatLngValue(val) {
      if (!val || mapService.isMultiPolygon(val)) return null;
      return mapService.geojsonPolygonToLatLng(val);
    },
    checkPdfPlugin() {
      if (this.isPdfInPlugins && !this.pdfSheetPlugin) {
        if (this.value && this.value.geometry) {
          this.addPdfPlugin();
        } else {
          console.warn(`Map value is ${this.value}, so pdfPlugin can't be displayed`);
        }
      }

      if (this.pdfSheetPlugin && (!this.isPdfInPlugins || !this.value)) {
        this.removePdfPlugin();
      }
    },
    addPdfPlugin() {
      this.pdfSheetPlugin = mapService.addPdfPlugin(this.mapObject, this.value.geometry);
      this.updateMapPlugin({ id: 'pdf', options: this.pdfSheetPluginOptions });
      this.$emit('plugin:added', 'pdf');
    },
    removePdfPlugin() {
      mapService.removePdfPlugin(this.mapObject, this.pdfSheetPlugin);
      this.pdfSheetPlugin = null;
      this.$emit('plugin:removed', 'pdf');
    },
    onPdfChange() {
      if (this.pdfSheetPlugin) {
        this.$nextTick(() => {
          this.updateMapPlugin({
            id: 'pdf',
            options: this.pdfSheetPluginOptions,
          });
        });
      }
    },
    redraw: debounce(function () {
      if (this.$refs.map) {
        this.$refs.map.mapObject.invalidateSize({ animate: true });
      }
    }, 800),
  },
};
</script>

<style lang="scss" scoped>
.app-map {
  z-index: 5;

  &::v-deep .leaflet-control-attribution a:hover {
    text-decoration: none;
  }

  &::v-deep .leaflet-attribution-flag {
    display: none !important;
  }

  &::v-deep .leaflet-right {
    z-index: 900;
  }
}
</style>
